import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, distinctUntilChanged, forkJoin, map, of, startWith, switchMap } from 'rxjs';
import { CompanyContextService } from '@core/context/company-context.service';
import { AlertService } from '@core/services/alert.service';
import { DarkModeService } from '@core/services/dark-mode.service';
import { ModalService } from '@core/services/modal.service';
import { ProductNotificationsService } from '@core/services/product-notifications.service';
import { SidebarService } from '@core/services/sidebar.service';
import { formatCurrency } from '@core/utils/number.utils';
import { ProductNotification } from '@core/interfaces/product-notification.interface';
import { CashRegister } from '@models/cashRegister.interface';
import { ICashSession, ICashSessionPaymentMethodsSummary } from '@models/cashSession.interface';
import { Company } from '@models/company.interface';
import { User } from '@models/user.interface';
import { AuthService } from '@modules/auth/auth.service';
import { CompanyService } from '@modules/company/company.service';
import { CashRegisterService } from '@modules/cash/cash-register/cash-register.service';
import {
  CloseCashSessionModalComponent,
  CloseCashSessionModalResult,
} from '@modules/cash/cash-session/components/close-cash-session-modal/close-cash-session-modal.component';
import {
  OpenCashSessionModalComponent,
  OpenCashSessionModalResult,
} from '@modules/cash/cash-session/components/open-cash-session-modal/open-cash-session-modal.component';
import { CashSessionService } from '@modules/cash/cash-session/cash-session.service';
import { EmployeeTaskRealtimeService } from '@modules/employee/employee-task-realtime.service';
import { SyncStatusComponent } from '@shared/components/sync-status/sync-status.component';
import { CashSessionEventService } from '@core/services/cash-session-event.service';
import { hasAdminRole } from '@modules/my-account/utils/account-role.utils';

type CashAvailabilityState = {
  allActiveCashRegistersOccupied: boolean;
  isLoading: boolean;
};

@Component({
    selector: 'app-header',
    imports: [CommonModule, SyncStatusComponent],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [CashRegisterService, CompanyService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  darkModeService: DarkModeService = inject(DarkModeService);
  authService: AuthService = inject(AuthService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly alertService = inject(AlertService);
  private readonly cashRegisterService = inject(CashRegisterService);
  private readonly cashSessionService = inject(CashSessionService);
  private readonly companyService = inject(CompanyService);
  private readonly modalService = inject(ModalService);
  readonly productNotificationsService = inject(ProductNotificationsService);
  private readonly employeeTaskRealtimeService = inject(EmployeeTaskRealtimeService);
  private readonly cashSessionEventService = inject(CashSessionEventService);
  readonly sidebarService = inject(SidebarService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly reloadVersion = signal(0);
  readonly isMobileViewport = toSignal(this.sidebarService.isMobileViewport$, {
    initialValue: this.sidebarService.isMobileViewport,
  });
  readonly isMobileMenuOpen = toSignal(this.sidebarService.isMobileOpen$, {
    initialValue: this.sidebarService.isMobileOpen,
  });
  readonly isMobileSidebarOpen = computed(
    () => this.isMobileViewport() && this.isMobileMenuOpen(),
  );

  readonly formatCurrency = formatCurrency;
  isDarkMode = this.darkModeService.darkMode;
  isNotificationsOpen = false;
  isUserMenuOpen = false;
  isCashSessionMenuOpen = false;
  readonly branches = signal<Company[]>([]);
  readonly branchesLoading = signal(false);
  readonly branchesLoaded = signal(false);

  readonly company = toSignal(this.companyContextService.company$, {
    initialValue: this.companyContextService.value,
  });
  readonly currentUser = toSignal(this.authService.user$, {
    initialValue: this.authService.currentUser,
  });
  readonly isAdmin = computed(() => hasAdminRole(this.currentUser()));
  readonly currentUserLabel = computed(() =>
    this.formatCurrentUserLabel(this.currentUser()),
  );
  readonly businessIdFromContext = computed(() => {
    const company = this.company();
    return company ? Number(company.business_id) : null;
  });
  readonly currentBranchLabel = computed(() => {
    const name = this.company()?.name?.trim();
    return name || 'Sin sucursal';
  });
  readonly hasMultipleBranches = computed(() => this.branches().length > 1);
  // Session and payment summary come from one shared `GET /current` request.
  private readonly currentSessionState = toSignal(
    toObservable(
      computed(() => ({
        businessId: this.businessIdFromContext(),
        version: this.reloadVersion(),
      })),
    ).pipe(
      distinctUntilChanged(
        (prev, curr) =>
          prev.businessId === curr.businessId && prev.version === curr.version,
      ),
      switchMap(({ businessId }) => {
        if (!businessId) {
          return of({
            session: null as ICashSession | null,
            summary: null as ICashSessionPaymentMethodsSummary | null,
            isLoading: false,
          });
        }

        return this.cashSessionService.getCurrentCashSessionWithPaymentMethods().pipe(
          map(({ session, summary }) => ({
            session,
            summary,
            isLoading: false,
          })),
          startWith({
            session: null as ICashSession | null,
            summary: null as ICashSessionPaymentMethodsSummary | null,
            isLoading: true,
          }),
          catchError(() =>
            of({
              session: null as ICashSession | null,
              summary: null as ICashSessionPaymentMethodsSummary | null,
              isLoading: false,
            }),
          ),
        );
      }),
    ),
    {
      // Loading until the first answer, so the open-sessions list is not
      // requested while the user's own session is still unknown.
      initialValue: {
        session: null as ICashSession | null,
        summary: null as ICashSessionPaymentMethodsSummary | null,
        isLoading: true,
      },
    },
  );
  readonly currentSession = computed(() => this.currentSessionState().session);
  readonly hasActiveCashSession = computed(
    () => this.currentSession()?.status === 'OPEN',
  );
  private readonly cashAvailabilityState = toSignal(
    toObservable(
      computed(() => ({
        companyId: String(this.company()?.id ?? '').trim(),
        businessId: this.businessIdFromContext(),
        hasActiveSession: this.hasActiveCashSession(),
        sessionLoading: this.currentSessionState().isLoading,
        version: this.reloadVersion(),
      })),
    ).pipe(
      distinctUntilChanged(
        (prev, curr) =>
          prev.companyId === curr.companyId &&
          prev.businessId === curr.businessId &&
          prev.hasActiveSession === curr.hasActiveSession &&
          prev.sessionLoading === curr.sessionLoading &&
          prev.version === curr.version,
      ),
      switchMap(({ companyId, businessId, hasActiveSession, sessionLoading }) => {
        // Only needed to tell "all registers busy" when the user has no session.
        if (!companyId || !businessId || hasActiveSession || sessionLoading) {
          return of({
            allActiveCashRegistersOccupied: false,
            isLoading: false,
          } satisfies CashAvailabilityState);
        }

        return forkJoin({
          cashRegisters: this.cashRegisterService.getAll({
            company_id: companyId,
            business_id: String(businessId),
          }),
          openSessions: this.cashSessionService
            .getCashSessions({
              page: 1,
              // Backend max page size.
              limit: 100,
              status: 'OPEN',
              sortBy: 'created_at',
              sort_order: 'DESC',
            })
            .pipe(map((response) => response.data?.data ?? [])),
        }).pipe(
          map(({ cashRegisters, openSessions }) => {
            const activeCashRegisters = cashRegisters.filter(
              (cashRegister) =>
                cashRegister.is_active &&
                String(cashRegister.company_id) === companyId,
            );
            const occupiedCashRegisterIds = new Set(
              openSessions
                .map((session) => Number(session.cash_register_id ?? 0))
                .filter((cashRegisterId) => cashRegisterId > 0),
            );

            return {
              allActiveCashRegistersOccupied:
                activeCashRegisters.length > 0 &&
                activeCashRegisters.every((cashRegister) =>
                  occupiedCashRegisterIds.has(Number(cashRegister.id)),
                ),
              isLoading: false,
            } satisfies CashAvailabilityState;
          }),
          startWith({
            allActiveCashRegistersOccupied: false,
            isLoading: true,
          } satisfies CashAvailabilityState),
          catchError(() =>
            of({
              allActiveCashRegistersOccupied: false,
              isLoading: false,
            } satisfies CashAvailabilityState),
          ),
        );
      }),
    ),
    {
      initialValue: {
        allActiveCashRegistersOccupied: false,
        isLoading: false,
      } satisfies CashAvailabilityState,
    },
  );
  readonly allActiveCashRegistersOccupied = computed(
    () => this.cashAvailabilityState().allActiveCashRegistersOccupied,
  );
  readonly currentCashRegisterId = computed(
    () =>
      Number(
        this.currentSession()?.cash_register_id ??
          this.currentSession()?.cashRegisterObject?.id ??
          0,
      ) || null,
  );
  private readonly currentCashRegisterState = toSignal(
    toObservable(
      computed(() => ({
        cashRegisterId: this.currentCashRegisterId(),
        cashRegisterObject: this.currentSession()?.cashRegisterObject ?? null,
        hasActiveSession: this.hasActiveCashSession(),
        version: this.reloadVersion(),
      })),
    ).pipe(
      distinctUntilChanged(
        (prev, curr) =>
          prev.cashRegisterId === curr.cashRegisterId &&
          prev.cashRegisterObject?.id === curr.cashRegisterObject?.id &&
          prev.hasActiveSession === curr.hasActiveSession &&
          prev.version === curr.version,
      ),
      switchMap(({ cashRegisterId, cashRegisterObject, hasActiveSession }) => {
        if (!hasActiveSession) {
          return of(null as CashRegister | null);
        }

        if (cashRegisterObject?.id) {
          return of(cashRegisterObject as CashRegister);
        }

        if (!cashRegisterId) {
          return of(null as CashRegister | null);
        }

        return this.cashRegisterService.getById(cashRegisterId).pipe(
          catchError(() => of(null as CashRegister | null)),
        );
      }),
    ),
    { initialValue: null as CashRegister | null },
  );
  readonly currentCashRegister = computed(
    () =>
      (this.currentSession()?.cashRegisterObject as CashRegister | null) ??
      this.currentCashRegisterState(),
  );
  private readonly currentCashSummary = computed(() =>
    this.hasActiveCashSession() ? this.currentSessionState().summary : null,
  );
  readonly cashSessionRegisterLabel = computed(() =>
    this.formatCashRegisterLabel(this.currentCashRegister()),
  );
  readonly cashSessionOpenedBy = computed(() =>
    this.formatUserName(this.currentSession()?.openedByUserObject),
  );
  readonly cashSessionCashAmount = computed(() =>
    Number(
      this.currentCashSummary()?.payment_methods.cash.expected_amount ??
        this.currentSession()?.opening_amount ??
        0,
    ),
  );
  readonly cashIndicatorLabel = computed(() =>
    this.hasActiveCashSession()
      ? 'Caja abierta'
      : this.allActiveCashRegistersOccupied()
        ? 'Cajas ocupadas'
        : 'Sin caja abierta',
  );
  readonly cashIndicatorDotClass = computed(() => {
    if (this.hasActiveCashSession()) {
      return 'bg-emerald-500';
    }

    if (this.allActiveCashRegistersOccupied()) {
      return 'bg-amber-400';
    }

    return 'bg-red-500';
  });
  readonly cashIndicatorTooltip = computed(() => {
    if (!this.hasActiveCashSession()) {
      return this.allActiveCashRegistersOccupied()
        ? 'No tienes caja abierta y todas las cajas están ocupadas'
        : 'No hay caja abierta';
    }

    return `${this.cashIndicatorLabel()} · ${this.cashSessionRegisterLabel()}`;
  });
  readonly productNotifications = this.productNotificationsService.notifications;
  readonly productNotificationsCount =
    this.productNotificationsService.notificationsCount;
  readonly hasProductNotifications =
    this.productNotificationsService.hasNotifications;
  readonly canDisplayProductNotifications =
    this.productNotificationsService.canDisplayNotifications;
  readonly notificationCountLabel = computed(() => {
    const count = this.productNotificationsCount();
    return count > 99 ? '99+' : String(count);
  });

  constructor() {
    this.cashSessionEventService.sessionChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reloadVersion.update((v) => v + 1);
      });
  }

  toggleDarkMode(): void {
    this.darkModeService.toggleDarkMode();
  }

  toggleSidebarMenu(): void {
    this.sidebarService.toggleMobile();
  }

  toggleCashSessionMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isCashSessionMenuOpen = !this.isCashSessionMenuOpen;

    if (this.isCashSessionMenuOpen) {
      this.isNotificationsOpen = false;
      this.isUserMenuOpen = false;
    }
  }

  closeCashSessionMenu(): void {
    this.isCashSessionMenuOpen = false;
  }

  toggleNotifications(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canDisplayProductNotifications()) {
      this.isNotificationsOpen = false;
      return;
    }
    this.isNotificationsOpen = !this.isNotificationsOpen;
    if (this.isNotificationsOpen) {
      this.isUserMenuOpen = false;
      this.isCashSessionMenuOpen = false;
    }
  }

  dismissNotification(
    notification: ProductNotification,
    event?: MouseEvent,
  ): void {
    event?.stopPropagation();
    this.productNotificationsService.dismissNotification(notification);
  }

  dismissAllNotifications(event?: MouseEvent): void {
    event?.stopPropagation();
    this.productNotificationsService.dismissAllNotifications();
  }

  openNotification(
    notification: ProductNotification,
    event?: MouseEvent,
  ): void {
    event?.stopPropagation();
    this.closeMenus();
    void this.router.navigate(notification.routeCommands);
  }

  notificationIconClass(notification: ProductNotification): string {
    if (notification.type === 'LOW_STOCK') {
      return 'fa-solid fa-triangle-exclamation text-amber-500';
    }

    if (notification.type === 'OUT_OF_STOCK') {
      return 'fa-solid fa-triangle-exclamation text-rose-500';
    }

    if (notification.type === 'EMPLOYEE_TASK_ASSIGNED') {
      return 'fa-solid fa-list-check text-emerald-500';
    }

    if (notification.type === 'RESOURCE_ADDON_ADDED') {
      return 'fa-solid fa-circle-plus text-emerald-500';
    }

    return 'fa-solid fa-circle-info text-sky-500';
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isUserMenuOpen = !this.isUserMenuOpen;
    if (this.isUserMenuOpen) {
      this.isNotificationsOpen = false;
      this.isCashSessionMenuOpen = false;
      this.loadBranches();
    }
  }

  selectBranch(event: Event): void {
    const branchId = (event.target as HTMLSelectElement | null)?.value;
    const branch = this.branches().find(
      (candidate) => String(candidate.id) === String(branchId),
    );

    if (!branch) {
      return;
    }

    this.companyContextService.setCompany(branch);
  }

  @HostListener('document:click')
  closeMenus(): void {
    this.isCashSessionMenuOpen = false;
    this.isNotificationsOpen = false;
    this.isUserMenuOpen = false;
  }

  goToCashSession(event?: MouseEvent): void {
    event?.stopPropagation();
    this.closeCashSessionMenu();
    const cashRegisterId = Number(this.currentSession()?.cash_register_id ?? 0);
    if (cashRegisterId > 0) {
      this.router.navigate(['/cash/register', cashRegisterId]);
    } else {
      this.router.navigate(['/cash/register']);
    }
  }

  openCashSessionModal(event?: MouseEvent): void {
    event?.stopPropagation();
    this.closeCashSessionMenu();

    if (!this.businessIdFromContext()) {
      this.alertService.open({
        type: 'warning',
        message: 'Selecciona un negocio antes de operar la caja.',
      });
      return;
    }

    this.modalService.open({
      component: OpenCashSessionModalComponent,
      title: 'Abrir caja',
      porpierties: {
        width: 'xl',
        closable: true,
      },
      data: {
        onSaved: (result: OpenCashSessionModalResult) => {
          this.alertService.open({
            type: result.type,
            message: result.message,
          });

          if (result.type !== 'error') {
            this.reloadVersion.update((value) => value + 1);
          }
        },
      },
    });
  }

  openCloseCashSessionModal(event?: MouseEvent): void {
    event?.stopPropagation();
    this.closeCashSessionMenu();

    const session = this.currentSession();

    if (!session || session.status !== 'OPEN') {
      this.alertService.open({
        type: 'warning',
        message: 'No hay una caja abierta para cerrar.',
      });
      return;
    }

    this.modalService.open({
      component: CloseCashSessionModalComponent,
      title: `Cerrar caja #${session.id}`,
      porpierties: {
        width: '3xl',
        height: 'fit',
        closable: true,
      },
      data: {
        session,
        onSaved: (result: CloseCashSessionModalResult) => {
          this.alertService.open({
            type: result.type,
            message: result.message,
          });

          if (result.type !== 'error') {
            this.reloadVersion.update((value) => value + 1);
          }
        },
      },
    });
  }

  signOut(): void {
    this.authService.logout();
  }

  goToMyAccount(event?: MouseEvent): void {
    event?.stopPropagation();
    this.closeMenus();
    void this.router.navigate(['/my-account']);
  }

  goToBilling(event?: MouseEvent): void {
    event?.stopPropagation();
    this.closeMenus();
    void this.router.navigate(['/my-account/billing']);
  }

  private loadBranches(): void {
    if (this.branchesLoaded() || this.branchesLoading()) {
      return;
    }

    this.branchesLoading.set(true);
    this.companyService
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (branches) => {
          this.branches.set(branches);
          this.branchesLoaded.set(true);
          this.branchesLoading.set(false);
        },
        error: () => {
          this.branchesLoading.set(false);
        },
      });
  }

  private formatUserName(user?: User | null): string {
    if (!user) {
      return 'No disponible';
    }

    const fullName = [user.name, user.father_lastname, user.mother_lastname]
      .filter(Boolean)
      .join(' ')
      .trim();

    return fullName || user.email || `Usuario #${user.id}`;
  }

  private formatCurrentUserLabel(user?: User | null): string {
    const fullName = this.formatUserName(user).trim();
    if (fullName && fullName !== 'No disponible') {
      return fullName;
    }

    const storedName = localStorage.getItem('name')?.trim();
    return storedName || 'Usuario';
  }

  private formatCashRegisterLabel(
    cashRegister?: ICashSession['cashRegisterObject'],
  ): string {
    const code = String(cashRegister?.code ?? '').trim();
    const name = String(cashRegister?.name ?? '').trim();

    if (code && name) {
      return `${code} · ${name}`;
    }

    if (code) {
      return code;
    }

    if (name) {
      return name;
    }

    return 'Caja principal';
  }
}
